import { useState } from 'react';
import { CheckCircle2, ClipboardCopy, RefreshCw } from 'lucide-react';
import clsx from 'clsx';

/**
 * Shared shell for the `generate/*` family (uuid / password / nanoid).
 *
 * Layout:
 *   ┌─ controls slot ─────────────────────── [重新生成] [全部复制] ─┐
 *   │  (toolboxes drop their parameters here)                    │
 *   ├──────── error footer (only if error non-empty) ────────────┤
 *   │  · item 1                                          [复制]   │
 *   │  · item 2                                          [复制]   │
 *   │  ...                                                       │
 *   └────────────────────────────────────────────────────────────┘
 *
 * Why a wrapper rather than fully inline: every generator tool needs
 * the same toolbar slot + bulk-copy + per-row copy with 1s green-check
 * feedback + empty-state placeholder. Three pages writing this logic
 * three times invariably drifts. The shell knows nothing about the
 * generation pipeline — pages compute `items` themselves and decide
 * when to bump it (params change → setItems(generate())).
 *
 * R2 reminder: nothing here touches localStorage / sessionStorage.
 * Generated values are working buffer; persistence belongs to the
 * tool's own opt-in "saved presets" feature (P1).
 */
export interface GeneratorShellProps {
  /** Tool-specific parameter form (left side of the toolbar). */
  controls: React.ReactNode;
  /**
   * Generated items. Empty array → placeholder; non-empty → list with
   * per-row copy buttons. The shell intentionally doesn't generate
   * anything itself; the page recomputes on param change and sets
   * this prop.
   */
  items: string[];
  /** Validation / generation error. Non-empty hides the list. */
  error?: string;
  /** "重新生成" handler — re-roll with the same params. */
  onRegenerate: () => void;
  /** Separator used by "全部复制". Defaults to '\n'. */
  copyAllSeparator?: string;
  /** Hint shown when items is empty AND error is unset. */
  emptyHint?: string;
}

export function GeneratorShell({
  controls,
  items,
  error,
  onRegenerate,
  copyAllSeparator = '\n',
  emptyHint = '— 调整参数后会立即生成 —',
}: GeneratorShellProps): JSX.Element {
  const [copyAllState, setCopyAllState] = useState<'idle' | 'ok'>('idle');

  const onCopyAll = async (): Promise<void> => {
    if (items.length === 0) return;
    try {
      await navigator.clipboard.writeText(items.join(copyAllSeparator));
      setCopyAllState('ok');
      window.setTimeout(() => setCopyAllState('idle'), 1000);
    } catch {
      /* ignore — page-level toast not warranted for a niche failure */
    }
  };

  return (
    <section className="flex min-h-0 min-w-0 flex-col overflow-hidden rounded-md border border-line bg-bg-card">
      <header className="flex flex-wrap items-center gap-2 border-b border-line px-3 py-2">
        <div className="flex flex-wrap items-center gap-2">{controls}</div>
        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            onClick={onRegenerate}
            className="inline-flex items-center gap-1 rounded-md border border-line px-2 py-0.5 text-[11.5px] font-medium text-ink-muted hover:border-accent/60 hover:text-accent"
            title="重新生成（Space）"
          >
            <RefreshCw className="h-3 w-3" />
            重新生成
          </button>
          <button
            type="button"
            onClick={onCopyAll}
            disabled={items.length === 0}
            className={clsx(
              'inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11.5px] font-medium transition',
              items.length === 0
                ? 'cursor-not-allowed border border-line/60 text-ink-dim'
                : copyAllState === 'ok'
                  ? 'border border-emerald-500/50 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300'
                  : 'border border-line text-ink-muted hover:border-accent/60 hover:text-accent',
            )}
            title="全部复制（一行一项）"
          >
            {copyAllState === 'ok' ? (
              <CheckCircle2 className="h-3 w-3" />
            ) : (
              <ClipboardCopy className="h-3 w-3" />
            )}
            {copyAllState === 'ok' ? '已复制' : `全部复制 (${items.length})`}
          </button>
        </div>
      </header>

      {error && (
        <p className="mx-3 my-2 rounded-md border border-rose-500/30 bg-rose-500/10 px-3 py-1.5 text-[12px] text-rose-700 dark:text-rose-300">
          {error}
        </p>
      )}

      <div className="min-h-0 flex-1 overflow-auto px-2 py-1.5 font-mono text-[12.5px]">
        {items.length === 0 && !error ? (
          <div className="flex h-full items-center justify-center text-[12px] font-sans text-ink-dim">
            {emptyHint}
          </div>
        ) : (
          items.map((item, i) => <Row key={`${i}-${item}`} value={item} />)
        )}
      </div>
    </section>
  );
}

function Row({ value }: { value: string }): JSX.Element {
  const [copied, setCopied] = useState(false);
  const onCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 900);
    } catch {
      /* ignore */
    }
  };
  return (
    <div className="group flex items-center gap-2 rounded px-2 py-0.5 hover:bg-bg-soft">
      <span className="text-ink-dim">·</span>
      <code className="min-w-0 flex-1 select-text break-all text-ink">{value}</code>
      <button
        type="button"
        onClick={onCopy}
        className="shrink-0 rounded p-0.5 text-ink-dim opacity-0 transition group-hover:opacity-100 hover:bg-bg-soft hover:text-accent"
        title="复制"
        aria-label="复制此条"
      >
        {copied ? (
          <CheckCircle2 className="h-3 w-3 text-emerald-500" />
        ) : (
          <ClipboardCopy className="h-3 w-3" />
        )}
      </button>
    </div>
  );
}
